import { Link, usePage } from "@inertiajs/react";
import I from "../Icons";
import { pick } from "../../lib/i18n";
import type { FeatureFlags } from "../../types";

const copy = {
  pl: { workflow: "Przepływ", upload: "Wgraj i analizuj", generate: "Generacja", results: "Wyniki", products: "Produkty", library: "Biblioteka", catalogs: "Katalogi", history: "Historia", myTokenUsage: "Moje zużycie tokenów", administration: "Administracja", brands: "Marki", personas: "Persony modeli", users: "Użytkownicy", reports: "Raporty", errors: "Błędy", settings: "Ustawienia", promptTemplates: "Szablony promptów", myAccount: "Moje konto", myAccountEdit: "Moje konto - kliknij aby edytować" },
  en: { workflow: "Workflow", upload: "Upload & parse", generate: "Generation", results: "Results", products: "Products", library: "Library", catalogs: "Catalogs", history: "History", myTokenUsage: "My token usage", administration: "Administration", brands: "Brands", personas: "Model personas", users: "Users", reports: "Reports", errors: "Errors", settings: "Settings", promptTemplates: "Prompt templates", myAccount: "My account", myAccountEdit: "My account - click to edit" },
};

interface Product {
  id: number;
  name: string;
  updated_at?: string | undefined;
}

interface Props {
  products?: Product[] | undefined;
  currentProductId?: number | undefined;
  activeStep?: string | undefined;
  onStepClick?: ((step: string) => void) | undefined;
  user?: { name: string; initials: string; role?: string; credits_used?: number | undefined; credits_total?: number | undefined } | undefined;
}

export default function Sidebar({ products = [], currentProductId, activeStep, onStepClick, user }: Props) {
  const { url, props } = usePage<{ feature_flags?: FeatureFlags }>();
  const featureFlags = props.feature_flags;
  const areBrandsEnabled = featureFlags?.brands_enabled ?? true;
  const areCatalogsEnabled = featureFlags?.catalogs_enabled ?? true;
  const arePersonasEnabled = featureFlags?.personas_enabled ?? false;
  const t = pick(copy);

  const steps: [string, string, string][] = [
    ["upload", t.upload, "01"],
    ["generate", t.generate, "02"],
    ["results", t.results, "03"],
  ];

  const isAdmin = user?.role === "admin";
  const isAdminOrModerator = user?.role === "admin" || user?.role === "moderator";

  const isActive = (path: string) => url.startsWith(path);

  return (
    <div className="sidebar">
      <div className="scroll-area">
        {activeStep && (
          <>
            <div className="sb-section"><span>{t.workflow}</span></div>
            <div className="sb-list">
              {steps.map(([k, l, n]) => (
                <button
                  key={k}
                  type="button"
                  className={"sb-item" + (activeStep === k ? " active" : "")}
                  onClick={() => onStepClick?.(k)}
                >
                  <span className="mono" style={{ fontSize: 10, color: "var(--muted-2)", width: 18 }}>{n}</span>
                  <span>{l}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <Link
          href="/app"
          className={"sb-section sb-section-link" + (isActive("/app") ? " active" : "")}
          style={{ marginTop: 8 }}
        >
          <span>{t.products}</span>
          <span className="badge">{products.length}</span>
        </Link>
        <div className="sb-list">
          {products.map((p) => (
            <Link key={p.id} href={`/products/${p.id}`} className={"sb-item" + (p.id === currentProductId ? " active" : "")}>
              <span className="dot green" />
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
            </Link>
          ))}
        </div>

        <div className="sb-section" style={{ marginTop: 8 }}><span>{t.library}</span></div>
        <div className="sb-list">
          {areCatalogsEnabled && (
            <Link href="/catalogs" className={"sb-item" + (isActive("/catalogs") ? " active" : "")}>
              {I.layers} {t.catalogs}
            </Link>
          )}
          <div className="sb-item">{I.history} {t.history}</div>
          <Link href="/reports/token_usage" className={"sb-item" + (isActive("/reports/token_usage") ? " active" : "")}>
            <span style={{ width: 14 }}>▤</span>
            <span>{t.myTokenUsage}</span>
          </Link>
        </div>

        {isAdminOrModerator && (
          <>
            <div className="sb-section" style={{ marginTop: 8 }}><span>{t.administration}</span></div>
            <div className="sb-list">
              {areBrandsEnabled && (
                <Link href="/brands" className={"sb-item" + (isActive("/brands") ? " active" : "")}>
                  <span style={{ width: 14 }}>◈</span>
                  <span>{t.brands}</span>
                </Link>
              )}
              {arePersonasEnabled && (
                <Link href="/personas" className={"sb-item" + (isActive("/personas") ? " active" : "")}>
                  <span style={{ width: 14 }}>◐</span>
                  <span>{t.personas}</span>
                </Link>
              )}
              <Link href="/admin/users" className={"sb-item" + (isActive("/admin/users") ? " active" : "")}>
                <span style={{ width: 14 }}>◎</span>
                <span>{t.users}</span>
              </Link>
              {isAdmin && (
                <Link href="/admin/reports/token_usage" className={"sb-item" + (isActive("/admin/reports") ? " active" : "")}>
                  <span style={{ width: 14 }}>▦</span>
                  <span>{t.reports}</span>
                </Link>
              )}
              {isAdmin && (
                <Link href="/admin/errors" className={"sb-item" + (isActive("/admin/errors") ? " active" : "")}>
                  <span style={{ width: 14 }}>⚠</span>
                  <span>{t.errors}</span>
                </Link>
              )}
              {isAdmin && (
                <Link href="/admin/storage" className={"sb-item" + (isActive("/admin/storage") ? " active" : "")}>
                  <span style={{ width: 14 }}>⬡</span>
                  <span>Storage</span>
                </Link>
              )}
              {isAdmin && (
                <Link href="/settings" className={"sb-item" + (isActive("/settings") ? " active" : "")}>
                  <span style={{ width: 14 }}>⚙</span>
                  <span>{t.settings}</span>
                </Link>
              )}
              {isAdmin && (
                <Link href="/prompt_templates" className={"sb-item" + (isActive("/prompt_templates") ? " active" : "")}>
                  <span style={{ width: 14 }}>✎</span>
                  <span>{t.promptTemplates}</span>
                </Link>
              )}
            </div>
          </>
        )}
      </div>

      {user && (
        <Link href="/users/edit" className="sb-foot" style={{ textDecoration: "none" }} title={t.myAccountEdit}>
          <div className="avatar">{user.initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, color: "var(--ink)", fontWeight: 500 }}>{user.name}</div>
            <div style={{ fontSize: 10, color: "var(--muted-2)" }}>{t.myAccount}</div>
          </div>
          {I.edit}
        </Link>
      )}
    </div>
  );
}
